import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InformationModalComponent } from 'src/app/shared/information-modal/information-modal.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthSnapshotService } from 'src/app/store/auth/auth-snapshot.service';
import { decodeJwt } from 'src/app/store/auth/jwt.util';

/**
 * How long a session lasts when the user did not ask to be remembered.
 *
 * The previous code wrote this as `new Date(jwt.iat).getTime() + 30 * 24 * 60 * 60`.
 * `new Date(n).getTime()` returns `n` unchanged for a number, so that round trip
 * was a no-op and the arithmetic was plain seconds all along — just obscured.
 */
const UNREMEMBERED_SESSION_SECONDS = 30 * 24 * 60 * 60;

/**
 * Gates the authenticated areas of the app.
 *
 * Reads the session from the store rather than localStorage, and returns a UrlTree
 * instead of navigating as a side effect, so the router owns the redirect.
 *
 * Note this only bounds how long a session is honoured client-side. It is not an
 * authorization check: the token is the server's to accept or reject, and a 401 is
 * handled by ErrorHandlerService.
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const snap = inject(AuthSnapshotService);
  const authService = inject(AuthService);
  const modalService = inject(NgbModal);
  const router = inject(Router);

  // The attempted URL rides along, so signing in lands the user on the page they
  // pasted or bookmarked instead of the dashboard. login.component validates it as
  // a same-origin path before honouring it.
  const toLogin = () =>
    router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });

  if (!snap.user) return toLogin();

  // A user with no readable token authenticates nothing. This used to throw inside
  // a promise executor and hang navigation outright.
  const claims = decodeJwt(snap.token);
  if (!claims) return toLogin();

  const nowInSeconds = Date.now() / 1000;

  // Currently always false: login writes remember_me onto the profile response
  // rather than onto the user it saves, so the flag never reaches storage and this
  // guard always takes the branch below. Left intact so that fixing the write is a
  // self-contained change — it will start enforcing `exp`, and log people out.
  const rememberMe = snap.user.remember_me === true;

  if (rememberMe) {
    return claims.exp !== undefined && nowInSeconds > claims.exp ? toLogin() : true;
  }

  const validUntil = (claims.iat ?? 0) + UNREMEMBERED_SESSION_SECONDS;
  if (nowInSeconds <= validUntil) return true;

  await announceExpiredSession(modalService);
  authService.logout();
  return toLogin();
};

/**
 * Tells the user why they are being sent back to the login page.
 *
 * Settles however the dialog goes away. The previous version resolved only on an
 * explicit `true` from the modal, so dismissing it with Esc or a backdrop click
 * left the route pending forever.
 */
async function announceExpiredSession(modalService: NgbModal): Promise<void> {
  const modalRef = modalService.open(InformationModalComponent, {
    size: 'md',
    windowClass: 'modal-bg-transparent',
  });
  modalRef.componentInstance.labelHeader = 'Notice';
  modalRef.componentInstance.modalLabel =
    'Your session has expired, you will be redirected to the login page.';

  try {
    await modalRef.result;
  } catch {
    // Dismissed rather than closed - the redirect happens either way.
  }
}
